<template>
  <ViewContainer :title="$t('rev.validation.validation_other')">
    <v-card class="ma-0 pa-0 pb-8">
      <v-row class="ma-0 pr-6 pb-5">
        <v-col cols="12" sm="9" class="px-0">
          <div class="content-tabs">
            <v-tabs grow slider-size="4" v-model="activeFilters.activeTab">
              <v-tab
                active-class="grey--text text--darken-4"
                data-tourid="validationsView-tab-implementation"
                @click="
                  handleClickTab(
                    $constants.validationsFilters.contentTab.implementation
                  )
                "
              >
                {{ $t('common.implementation') }}
              </v-tab>
              <v-tab
                active-class="grey--text text--darken-4"
                data-tourid="validationsView-tab-impact"
                @click="
                  handleClickTab(
                    $constants.validationsFilters.contentTab.impact
                  )
                "
              >
                {{ $t('common.impact') }}
              </v-tab>
            </v-tabs>
          </div>
        </v-col>

        <v-col v-if="permissionToValidate" cols="12" sm="3" class="mt-5">
          <SearchInput
            data-testid="validationsRequestView-search-searchRevs"
            data-tourid="validationsRequestView-filter-searchBar"
            :disabled="isLoading"
            :value="searchTerms"
            @change="handleSearch"
          />
        </v-col>
      </v-row>
      <v-row v-if="!permissionToValidate" class="ma-0">
        <v-col>
          <EmptyState
            style="height: 420px"
            data-testid="validationsRequestView-emptyState"
            :icon="emptyStateIcon"
            :title="emptyStateTitle"
            :message="emptyStateNoPermissionMessage"
          />
        </v-col>
      </v-row>
      <v-col v-else class="ma-0">
        <v-row class="pl-8 pr-0 mt-2 mb-2">
          <Button
            size="small"
            depressed
            data-testid="valdations-button-toggleFilters"
            data-tourid="validations-button-toggleFilters"
            icon="mdi-tune"
            class="pl-2 pr-2 mb-4"
            :color="showFilters ? 'primary' : 'blueGrey'"
            :outlined="!showFilters"
            :text="toggleFiltersText"
            @click="handleToggleFilters"
          />
        </v-row>
        <v-row class="pa-0 mb-2">
          <v-col
            v-show="showFilters"
            class="pl-8 pr-0 ma-0"
            cols="12"
            :md="showFilters ? 3 : 12"
          >
            <ValidationsRequestFilters
              :filters="activeFilters"
              :isImplementationTab="
                activeFilters.activeTab ===
                  $constants.validationsFilters.contentTab.implementation
              "
              @change="handleFiltersChange"
            />
          </v-col>
          <v-col :md="showFilters ? 9 : 12" class="pl-6 pr-6 ma-0 pt-0">
            <v-flex class="w-100 justify-between align-center mb-2 d-flex">
              <p class="caption blue-grey--text text--darken-2 mb-2 pa-0">
                {{
                  $t('common.resultWithCount_other', {
                    count: totalRecordsInData,
                  })
                }}
              </p>
              <ColumnsSelector
                v-if="!isLoading && hasRevs"
                data-testid="explore-button-columnsSelector"
                :headers="dynamicHeaders"
                @toggleColumn="onToggleColumn"
              />
            </v-flex>
            <ValidationsRequestContentTable
              v-show="hasRevs || isLoading"
              :activeTab="activeTab"
              :organizationId="organizationId"
              :isLoading="isLoading"
              :totalItems="totalRecordsInData"
              :currentPage="currentPage"
              :itemsPerPage="itemsPerPage"
              :headers="headersToShow"
              :revs="revs"
              :sortBy="defaultSortBy"
              :sortDesc="defaultSortDesc"
              @fetchRevs="handleContentTableChange"
              @updateValidatedReviewField="updateValidatedReviewField"
            />
            <EmptyState
              v-if="!hasRevs && !isLoading"
              style="height: 420px"
              data-testid="validationsRequestView-emptyState"
              :icon="emptyStateIcon"
              :title="emptyStateTitle"
              :message="emptyStateMessage"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-card>
  </ViewContainer>
</template>

<script>
import { mapGetters } from 'vuex'
import mixin from '@/mixins/mixin.vue'
import revMixin from '@/mixins/revMixin'
import trackerMixin from '@/mixins/tracking/trackerMixin'

import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'
import EmptyState from '@/components/EmptyState.vue'
import ValidationsRequestContentTable from './ValidationsRequestContentTable.vue'
import Button from '@/components/UI/Button.vue'
import ValidationsRequestFilters from './ValidationRequestFilters.vue'
import ColumnsSelector from '@/components/ColumnsSelector.vue'

import _ from 'lodash'
import access from '@/lib/access'
import api from '@/api'

import SearchInput from '@/components/Core/SearchInput.vue'

const sortOrder = {
  ASCENDING: 'asc',
  DESCENDING: 'desc',
}

const ITEMS_PER_PAGE = 20

export default {
  name: 'ValidationsRequestView',

  mixins: [mixin, revMixin, trackerMixin],

  components: {
    ViewContainer,
    EmptyState,
    ValidationsRequestContentTable,
    SearchInput,
    Button,
    ValidationsRequestFilters,
    ColumnsSelector,
  },

  data() {
    return {
      searchTerms: this.$route.query.searchTerms ?? '',
      ITEMS_PER_PAGE,
      isLoading: true,
      totalRecordsInData: 0,
      revs: [],
      selectedFilter:
        this.$route.query.filter ??
        this.$constants.validationsFilters.validations.assignedToMe,
      currentPage: this.$route.query.page ? Number(this.$route.query.page) : 1,
      itemsPerPage: this.$route.query.itemsPerPage
        ? Number(this.$route.query.itemsPerPage)
        : ITEMS_PER_PAGE,
      sortBy: this.$route.query.sortBy ?? null,
      sortDesc: this.$route.query.sortDesc
        ? this.$route.query.sortDesc === 'true'
        : null,
      showFilters: true,
      activeFilters: {
        validation: this.$route.query.filter ?? null,
        sites: this.$route.query.tags ?? [],
        tagGroups: this.$route.query.tagGroups ?? [],
        siteGroups: this.$route.query.siteGroups ?? [],
        categories: this.getRouteCategories(),
        tags: [],
        activeTab: this.$route.query.activeTab
          ? Number(this.$route.query.activeTab)
          : null,
        role: this.getRouteRole(),
        countryCodes: this.$route.query.countryCodes ?? [],
      },
      dynamicHeaders: [],
    }
  },

  async mounted() {
    this.activeFilters = { ...this.activeFilters, ...this.setInitialFilters() }
  },

  watch: {
    activeFilters: {
      handler() {
        this.currentPage = 1
        this.trackValidationsView(this.activeFilters)
        this.fetchRevs({
          page: 1,
        })
      },
      deep: true,
    },

    currentPage(currentValue) {
      this.updateRoute({ page: currentValue })
    },
  },

  computed: {
    ...mapGetters([
      'accountId',
      'organizationId',
      'account',
      'organizationSites',
    ]),

    requestFilters() {
      const filters = {
        hasImplementationValidation:
          this.activeFilters.activeTab ===
          this.$constants.validationsFilters.contentTab.implementation
            ? this.activeFilters.validation ===
              this.$constants.validationsFilters.validations.validated
            : true,
        hasFinancialValidation:
          this.activeFilters.activeTab ===
          this.$constants.validationsFilters.contentTab.impact
            ? this.activeFilters.validation ===
              this.$constants.validationsFilters.validations.validated
            : false,
        status: this.$constants.revStatus.completed,
        sites: this.activeFilters.sites,
        siteGroups: this.activeFilters.siteGroups,
        tags: this.activeFilters.tags,
        tagGroups: this.activeFilters.tagGroups,
        categories: this.activeFilters.categories,
        countryCodes: this.activeFilters.countryCodes,
      }

      if (
        this.activeFilters.activeTab ===
          this.$constants.validationsFilters.contentTab.implementation &&
        this.activeFilters.role.includes('coach')
      )
        filters.coach = this.account?._id

      if (
        this.activeFilters.validation ===
        this.$constants.validationsFilters.validations.assignedToMe
      )
        return {
          ...filters,
          requestAssignees: [this.accountId],
          requestStatus: this.$constants.validationRequestStatus.inProgress,
        }

      return { ...filters }
    },

    permissionToValidate() {
      return this.activeFilters.activeTab ===
        this.$constants.validationsFilters.contentTab.impact
        ? access.isLeadershipOrFinancialCoach(this.account)
        : access.isLeadershipOrCoach(this.account)
    },

    emptyStateNoPermissionMessage() {
      return this.activeFilters.activeTab ===
        this.$constants.validationsFilters.contentTab.impact
        ? this.$t('validation.no_permission_impact_content')
        : this.$t('validation.no_permission_implementation_content')
    },

    showImpactValidationColumns() {
      return this.impactTabIsActive && this.validatedFilterIsSelected
    },

    showRequestedColumns() {
      return (
        this.impactTabIsActive &&
        (this.assignedToMeFilterIsSelected || this.pendingFilterIsSelected)
      )
    },

    showRequestStatus() {
      return this.impactTabIsActive && this.pendingFilterIsSelected
    },

    showAssigneeColumn() {
      return this.impactTabIsActive && this.pendingFilterIsSelected
    },

    showCoachColumn() {
      return this.assignedToMeFilterIsSelected || this.implementationTabIsActive
    },

    showValidateColumn() {
      return (
        this.impactTabIsActive &&
        (this.assignedToMeFilterIsSelected || this.pendingFilterIsSelected)
      )
    },

    showImplementColumn() {
      return this.implementationTabIsActive && this.pendingFilterIsSelected
    },

    showRequestValidationColumn() {
      return this.implementationTabIsActive && this.validatedFilterIsSelected
    },

    selectedHeaders() {
      return this.dynamicHeaders.filter(header => header.show === true)
    },

    activeTab() {
      return this.activeFilters.activeTab
    },

    implementationTabIsActive() {
      return (
        this.activeTab ===
        this.$constants.validationsFilters.contentTab.implementation
      )
    },

    impactTabIsActive() {
      return (
        this.activeTab === this.$constants.validationsFilters.contentTab.impact
      )
    },

    pendingFilterIsSelected() {
      return Boolean(
        this.selectedFilter ===
          this.$constants.validationsFilters.validations.pending
      )
    },

    assignedToMeFilterIsSelected() {
      return Boolean(
        this.selectedFilter ===
          this.$constants.validationsFilters.validations.assignedToMe
      )
    },

    validatedFilterIsSelected() {
      return Boolean(
        this.selectedFilter ===
          this.$constants.validationsFilters.validations.validated
      )
    },

    emptyStateTitle() {
      return this.searchTerms?.trim()
        ? this.$t('rev.validation.there_are_no_revs_to_show')
        : this.$t('common.theres_nothing_to_see_here')
    },

    emptyStateMessage() {
      if (this.searchTerms?.trim()) {
        return this.$t('rev.validation.search_term_empty_state')
      }
      return this.pendingFilterIsSelected
        ? this.$t('rev.validation.there_are_no_revs_pending_to_validate_yet')
        : this.$t('rev.validation.there_are_no_revs_assigned_to_you_directly')
    },

    emptyStateIcon() {
      return this.searchTerms?.trim()
        ? 'mdi-ghost-outline'
        : 'mdi-beehive-outline'
    },

    hasRevs() {
      return !_.isEmpty(this.revs)
    },

    toggleFiltersText() {
      const title = this.showFilters
        ? this.$t('explore.hide_filters')
        : this.$t('explore.show_filters')

      return `${title}  (${this.activeFiltersCount})`
    },

    activeFiltersCount() {
      const activeFilters = _.omitBy(
        Object.keys(this.activeFilters),
        item =>
          item === 'activeTab' ||
          (item === 'role' && this.impactTabIsActive) ||
          _.isEmpty(this.activeFilters[item])
      )
      return Object.values(activeFilters).length
    },

    headersToShow() {
      return this.selectedHeaders
    },

    implementedTabColumns() {
      return _.compact([
        {
          text: this.$t('common.name'),
          align: 'start',
          sortable: true,
          value: 'name',
          width: 367,
          show: true,
        },
        this.showImplementColumn && {
          text: this.$t('rev.validation.completed_on'),
          sortable: true,
          value: 'completedAt',
          align: 'center',
          show: true,
        },
        this.showRequestValidationColumn && {
          text: this.$t('rev.validation.implemented_on'),
          sortable: true,
          value: 'implementedAt',
          align: 'center',
          show: true,
        },
        {
          text: this.$t('common.type'),
          sortable: true,
          value: 'type',
          show: true,
        },
        this.implementationTabIsActive && {
          text: this.$t('common.roles.author'),
          sortable: true,
          value: 'author',
          show: true,
        },

        this.showCoachColumn && {
          text: this.$t('common.roles.coach_one'),
          sortable: true,
          value: 'coach',
          show: true,
        },
        this.implementationTabIsActive && {
          text: this.$t('common.tag_other'),
          sortable: true,
          value: 'tags',
          show: true,
        },
        this.showImplementColumn && {
          text: this.$t('rev.implement'),
          sortable: false,
          value: 'implement',
          align: 'center',
          class: 'fixed-column',
          cellClass: 'fixed-column',
          show: true,
        },
        this.showRequestValidationColumn && {
          text: this.$t('rev.validation.request_validation'),
          sortable: false,
          value: 'requestValidation',
          align: 'center',
          class: 'fixed-column',
          cellClass: 'fixed-column',
          show: true,
        },
      ])
    },

    impactColumns() {
      return _.compact([
        {
          text: this.$t('common.name'),
          align: 'start',
          sortable: true,
          value: 'name',
          width: 367,
          show: true,
        },
        {
          text: this.$t('common.type'),
          sortable: true,
          value: 'type',
          show: true,
        },
        this.showImpactValidationColumns && {
          text: this.$t('rev.validation.validated_by_header'),
          sortable: true,
          value: 'validatedBy',
          show: true,
        },
        this.showImpactValidationColumns && {
          text: this.$t('rev.validation.validation_date'),
          sortable: true,
          value: 'validatedAt',
          show: true,
        },
        this.showImpactValidationColumns && {
          text: this.$t('rev.validation.start_impact_period'),
          sortable: true,
          value: 'impactStartsAt',
          show: true,
        },
        this.showImpactValidationColumns && {
          text: this.$t('rev.validation.end_impact_period'),
          sortable: true,
          value: 'impactEndsAt',
          show: true,
        },
        this.showImpactValidationColumns && {
          text: this.$t('rev.validation.total_impact'),
          sortable: false,
          value: 'totalImpact',
          show: true,
          width: 200,
        },
        this.showImpactValidationColumns && {
          text: this.$t('rev.validation.total_impact_global'),
          sortable: true,
          value: 'totalImpactInGlobalCurrency',
          show: true,
          width: 200,
        },
        this.showImpactValidationColumns && {
          text: this.$t('rev.validation.view_validation'),
          sortable: false,
          value: 'viewValidation',
          show: true,
          class: 'fixed-column',
          cellClass: 'fixed-column',
        },
        this.showRequestStatus && {
          text: this.$t('rev.validation.validation_request'),
          sortable: true,
          value: 'requestStatus',
          show: true,
        },
        this.showRequestedColumns && {
          text: this.$t('rev.validation.requested_on'),
          sortable: true,
          value: 'requestCreatedAt',
          show: true,
        },
        this.showRequestedColumns && {
          text: this.$t('rev.validation.requested_by'),
          sortable: true,
          value: 'requestedBy',
          show: true,
        },
        this.showAssigneeColumn && {
          text: this.$t('common.assignee_one'),
          sortable: true,
          value: 'assignee',
          show: true,
        },
        this.showValidateColumn && {
          text: this.$t('rev.validation.validate'),
          sortable: false,
          value: 'validate',
          align: 'center',
          show: true,
          class: 'fixed-column',
          cellClass: 'fixed-column',
        },
      ])
    },

    defaultSortBy() {
      return (
        this.$route.query.sortBy ??
        (this.showRequestedColumns ? 'requestCreatedAt' : 'createdAt')
      )
    },

    defaultSortDesc() {
      const urlSortDesc = this.$route.query.sortDesc
      return urlSortDesc ? urlSortDesc === 'true' : true
    },
  },

  methods: {
    setDynamicColumns() {
      const isImpactTab =
        this.activeTab === this.$constants.validationsFilters.contentTab.impact
      if (isImpactTab) {
        this.dynamicHeaders = this.impactColumns
      } else {
        this.dynamicHeaders = this.implementedTabColumns
      }
    },

    getRouteRole() {
      const role = this.$route?.query?.role ?? []

      if (!_.isArray(this.$route.query.role) && this.$route.query.role)
        return [role]
      return role
    },

    getRouteCategories() {
      const categories = this.$route?.query?.categories ?? []

      if (!_.isArray(categories)) return [categories]
      return categories
    },

    async updateValidatedReviewField(data) {
      try {
        this.loading = true

        const updatedValidatedReview = await this.sendValidatedReviewUpdateRequest(
          {
            revId: data.revId,
            revType: data.revType,
            payload: [
              this.organizationId,
              data.revId,
              { ...data.fieldToUpdate },
            ],
          }
        )

        const updatedRevIndex = this.revs.findIndex(
          rev => rev.id === data.revId
        )
        const currentRev = { ...this.revs[updatedRevIndex] }

        if (_.has(data.fieldToUpdate, 'totalImpact')) {
          this.revs[updatedRevIndex] = {
            ...currentRev,
            validatedReview: updatedValidatedReview,
          }
        } else {
          this.revs[updatedRevIndex] = {
            ...currentRev,
            ...data.fieldToUpdate,
          }
        }

        this.revs = [...this.revs]
        this.showSuccess(this.$t('rev.save_success'))
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    async sendValidatedReviewUpdateRequest({ revId, payload, revType }) {
      let updateValidation = null
      let fetchRevById = null

      switch (revType) {
        case this.$constants.revTypes.improvementIdea:
          updateValidation = api.rev.savePartialValidation
          fetchRevById = api.rev.getById
          break
        case this.$constants.revTypes.quickAction:
          updateValidation = api.quickaction.savePartialValidation
          fetchRevById = api.quickaction.getById
          break
        case this.$constants.revTypes.problemSolving:
          updateValidation = api.problemsolving.savePartialValidation
          fetchRevById = api.problemsolving.getById
          break
      }

      await updateValidation(...payload)
      const { data } = await fetchRevById(this.organizationId, revId)

      return data?.validatedReview ?? {}
    },

    handleSearch(terms) {
      this.searchTerms = terms
      this.updateRoute({ searchTerms: terms })
      this.fetchRevs({ page: 1 })
    },

    fetchRevs: _.debounce(async function(options = {}) {
      try {
        this.isLoading = true
        const page = options?.page || this.$route.query.page || 1
        const size = options?.itemsPerPage || ITEMS_PER_PAGE
        const filters = this.getRequestFilters()
        const hasActiveSearch = Boolean(this.searchTerms)
        const isSortDescending = hasActiveSearch
          ? true
          : options?.sortDesc ?? this.defaultSortDesc
        const response = await api.explore.getContent({
          filters,
          organizationId: this.organizationId,
          terms: this.searchTerms,
          limit: options?.itemsPerPage || ITEMS_PER_PAGE,
          from: page * size - size,
          sort: {
            by: this.getSortBy(options?.sortBy),
            order: isSortDescending
              ? sortOrder.DESCENDING
              : sortOrder.ASCENDING,
          },
          expansions: [
            'requestCreatedBy',
            'requestAssignees',
            'validations',
            'coach',
            'author',
            'tags',
            'siteAttributes',
            'owner',
            'image',
            'site',
            'collaborators',
          ],
        })
        this.revs =
          response?.data?.results?.map(result => result?.response) ?? []
        this.totalRecordsInData = response?.data?.totalRecordsInData ?? 0
        this.setDynamicColumns()
        this.isLoading = false
      } catch (err) {
        this.handleRequestError(err)
      }
    }, 1000),

    getSortBy(sortBy = this.defaultSortBy) {
      if (this.searchTerms) return '_score'

      switch (sortBy) {
        case 'assignee':
          return 'requestAssignees'
        case 'requestedBy':
          return 'requestCreatedBy'
        case 'validatedBy':
          return 'validations.financial.auditor'
        case 'totalImpactInGlobalCurrency':
          return 'validationTotalImpactInGlobalCurrency'
        case 'validatedAt':
          return 'validations.financial.validatedAt'
        case 'implementedAt':
          return 'validations.implementation.validatedAt'
        default:
          return sortBy
      }
    },

    selectFilter(filter) {
      this.selectedFilter = filter
      this.updateRoute({ filter, sortBy: null, sortDesc: null })
      this.trackValidationsView(this.activeFilters)
      this.fetchRevs({
        page: 1,
      })
    },

    getRequestFilters() {
      const filters = {
        hasImplementationValidation:
          this.activeFilters.activeTab ===
          this.$constants.validationsFilters.contentTab.implementation
            ? this.activeFilters.validation ===
              this.$constants.validationsFilters.validations.validated
            : true,
        hasFinancialValidation:
          this.activeFilters.activeTab ===
          this.$constants.validationsFilters.contentTab.impact
            ? this.activeFilters.validation ===
              this.$constants.validationsFilters.validations.validated
            : false,
        status: [this.$constants.revStatus.completed],
        sites: this.activeFilters.sites,
        categories: this.activeFilters.categories,
        types: [
          this.$constants.exploreEntityTypes.improvementIdea,
          this.$constants.exploreEntityTypes.problemSolving,
          this.$constants.exploreEntityTypes.quickAction,
        ],
        tags: this.activeFilters.tags,
        countryCodes: this.activeFilters.countryCodes,
      }

      if (
        this.activeFilters.activeTab ===
          this.$constants.validationsFilters.contentTab.implementation &&
        this.activeFilters.role.includes('coach')
      )
        filters.coach = this.account?._id

      if (
        this.activeFilters.validation ===
        this.$constants.validationsFilters.validations.assignedToMe
      )
        return {
          ...filters,
          requestAssignees: [this.accountId],
          requestStatus: this.$constants.validationRequestStatus.inProgress,
        }

      return { ...filters }
    },

    async updateRoute(queryOption) {
      const newQuery = _.omitBy(
        {
          ...this.$route.query,
          ...queryOption,
        },
        _.isNull
      )
      await this.$router
        .replace({
          path: 'validations',
          query: newQuery,
        })
        .catch(() => {
          // This is intentional to avoid navigation duplicated error
        })
    },

    handleToggleFilters() {
      this.showFilters = !this.showFilters
    },

    handleFiltersChange({ filter, value }) {
      switch (filter) {
        case 'filter':
          this.activeFilters.validation = value
          this.selectedFilter = value
          this.currentPage = 1
          this.updateRoute({ filter: value, sortBy: null, sortDesc: null })
          break
        case 'sites':
          this.activeFilters.sites = value
          this.selectedSites = value
          this.updateRoute({ sites: value })
          break
        case 'categories':
          this.activeFilters.categories = value
          this.updateRoute({ categories: value })
          break
        case 'tags':
          this.activeFilters.tags = value
          this.updateRoute({ tags: value })
          break
        case 'role':
          this.activeFilters.role = value
          this.updateRoute({ role: value })
          break
        default:
          this.activeFilters[filter] = value
          this.updateRoute({ [filter]: value })
      }
    },

    handleContentTableChange(options) {
      this.currentPage = options?.page || 1
      this.updateRoute({
        page: this.currentPage,
        sortBy: options?.sortBy,
        sortDesc: options?.sortDesc,
      })
      this.fetchRevs(options)
    },

    handleClickTab(tab) {
      const isImpactTab =
        tab === this.$constants.validationsFilters.contentTab.impact

      this.activeFilters.validation = isImpactTab
        ? this.$constants.validationsFilters.validations.assignedToMe
        : this.$constants.validationsFilters.validations.pending

      this.updateRoute({
        activeTab: tab,
        filter: this.activeFilters.validation,
      })

      this.selectedFilter = isImpactTab
        ? this.$constants.validationsFilters.validations.assignedToMe
        : this.$constants.validationsFilters.validations.pending
    },

    onToggleColumn(event) {
      this.dynamicHeaders[event.index].show = event.value
      this.trackValidationsColumns({
        columns: this.dynamicHeaders,
        activeTab:
          this.activeTab ===
          this.$constants.validationsFilters.contentTab.impact
            ? 'impactTab'
            : 'implementationTab',
      })
    },

    setInitialFilters() {
      const initialFilters = {}
      if (_.isUndefined(this.$route.query.activeTab)) {
        initialFilters.activeTab = this.account.canDoFinancialValidation
          ? this.$constants.validationsFilters.contentTab.impact
          : this.$constants.validationsFilters.contentTab.implementation
      }

      if (!this.$route.query.filter) {
        initialFilters.validation =
          initialFilters.activeTab ===
          this.$constants.validationsFilters.contentTab.impact
            ? this.$constants.validationsFilters.validations.assignedToMe
            : this.$constants.validationsFilters.validations.pending
        this.selectedFilter = initialFilters.validation
      }

      this.setDynamicColumns()
      if (this.$route.query.sites) {
        const sites = this.organizationSites.filter(site => {
          return this.$route.query.sites?.includes(site._id)
        })
        this.selectedSites = sites.map(site => site._id)
        initialFilters.sites = this.selectedSites
      }

      if (this.$route.query.tags) {
        initialFilters.tags = _.isArray(this.$route.query.tags)
          ? this.$route.query.tags
          : [this.$route.query.tags]
      }

      return initialFilters
    },
  },
}
</script>

<style lang="scss" scoped>
.content-tabs {
  width: 400px !important;
}
</style>
